import {
  BitePorterApplication,
  BiteServiceName,
  BiteServicePorts,
  Environment,
} from '@biteinc/enums';

export interface BiteHttpServiceUrlConfig {
  readonly serviceName: BiteServiceName;
  readonly urlEnvVar: string;
  readonly internalUrlEnvVar: string;
  readonly port: number;
  readonly porterApplicationByEnv: Readonly<
    Record<Environment.PROD | Environment.STAGING | Environment.SANDBOX, BitePorterApplication>
  >;
}

const HTTP_SERVICE_URL_CONFIGS = [
  {
    serviceName: BiteServiceName.Bureau,
    urlEnvVar: 'BUREAU_URL',
    internalUrlEnvVar: 'BUREAU_INTERNAL_URL',
    port: BiteServicePorts.Bureau,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.BureauProd,
      [Environment.STAGING]: BitePorterApplication.BureauStaging,
      [Environment.SANDBOX]: BitePorterApplication.BureauSandbox,
    },
  },
  {
    serviceName: BiteServiceName.Flash,
    urlEnvVar: 'FLASH_URL',
    internalUrlEnvVar: 'FLASH_INTERNAL_URL',
    port: BiteServicePorts.Flash,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.FlashProd,
      [Environment.STAGING]: BitePorterApplication.FlashStaging,
      [Environment.SANDBOX]: BitePorterApplication.FlashSandbox,
    },
  },
  {
    serviceName: BiteServiceName.Garcon,
    urlEnvVar: 'GARCON_URL',
    internalUrlEnvVar: 'GARCON_INTERNAL_URL',
    port: BiteServicePorts.Garcon,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.GarconProd,
      [Environment.STAGING]: BitePorterApplication.GarconStaging,
      [Environment.SANDBOX]: BitePorterApplication.GarconSandbox,
    },
  },
  {
    serviceName: BiteServiceName.Maitred,
    urlEnvVar: 'MAITRED_URL',
    internalUrlEnvVar: 'MAITRED_INTERNAL_URL',
    port: BiteServicePorts.Maitred,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.MaitredProd,
      [Environment.STAGING]: BitePorterApplication.MaitredStaging,
      [Environment.SANDBOX]: BitePorterApplication.MaitredSandbox,
    },
  },
  {
    serviceName: BiteServiceName.RecommendationApp,
    urlEnvVar: 'RECOMMENDATION_APP_URL',
    internalUrlEnvVar: 'RECOMMENDATION_APP_INTERNAL_URL',
    port: BiteServicePorts.RecommendationApp,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.RecommendationAppProd,
      [Environment.STAGING]: BitePorterApplication.RecommendationAppStaging,
      [Environment.SANDBOX]: BitePorterApplication.RecommendationAppSandbox,
    },
  },
  {
    serviceName: BiteServiceName.Vitrine,
    urlEnvVar: 'VITRINE_URL',
    internalUrlEnvVar: 'VITRINE_INTERNAL_URL',
    port: BiteServicePorts.Vitrine,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.VitrineProd,
      [Environment.STAGING]: BitePorterApplication.VitrineStaging,
      [Environment.SANDBOX]: BitePorterApplication.VitrineSandbox,
    },
  },
  {
    serviceName: BiteServiceName.WebhookReceiver,
    urlEnvVar: 'WEBHOOK_RECEIVER_URL',
    internalUrlEnvVar: 'WEBHOOK_RECEIVER_INTERNAL_URL',
    port: BiteServicePorts.WebhookReceiver,
    porterApplicationByEnv: {
      [Environment.PROD]: BitePorterApplication.WebhookReceiverProd,
      [Environment.STAGING]: BitePorterApplication.WebhookReceiverStaging,
      [Environment.SANDBOX]: BitePorterApplication.WebhookReceiverSandbox,
    },
  },
] satisfies readonly BiteHttpServiceUrlConfig[];

const HTTP_SERVICE_URL_CONFIG_BY_NAME = new Map<BiteServiceName, BiteHttpServiceUrlConfig>(
  HTTP_SERVICE_URL_CONFIGS.map((config) => [config.serviceName, config]),
);

export function getHttpServiceUrlConfig(
  serviceName: BiteServiceName,
): BiteHttpServiceUrlConfig | undefined {
  return HTTP_SERVICE_URL_CONFIG_BY_NAME.get(serviceName);
}

export function httpServiceUrlConfigs(): readonly BiteHttpServiceUrlConfig[] {
  return HTTP_SERVICE_URL_CONFIGS;
}

const EXTRA_PROXY_URL_ENV_BY_PORT: Readonly<Record<number, string>> = {
  [BiteServicePorts.ChromeOsApp]: 'CHROMEOS_APP_URL',
  [BiteServicePorts.Pinata]: 'PINATA_URL',
  // mcp-app has no internal service-to-service callers, so it has no
  // HTTP_SERVICE_URL_CONFIGS entry — but `bite dev --proxy` still needs the
  // port → override mapping to hand out its portless URL.
  [BiteServicePorts.McpApp]: 'MCP_APP_URL',
  // No Porter application exists for eclair yet, so it cannot have an HTTP_SERVICE_URL_CONFIGS entry. Move it there when its Porter apps exist.
  [BiteServicePorts.Eclair]: 'ECLAIR_URL',
};

export function proxyUrlEnvByPort(): Readonly<Record<number, string>> {
  return {
    ...Object.fromEntries(
      HTTP_SERVICE_URL_CONFIGS.map((config) => [config.port, config.urlEnvVar]),
    ),
    ...EXTRA_PROXY_URL_ENV_BY_PORT,
  };
}
